import { useMemo } from "react";
import { motion } from "framer-motion";
import { STUDIO, mapsDirectionsUrl, streetViewUrl } from "../../constants/contact";

// Mapa propio, sin iframe ni librerías: se compone con las teselas del mapa
// oscuro de Esri (gratis y sin clave). Así no entran la barra de atribución de
// OpenStreetMap, los botones de zoom ni el corazón de donaciones, y el estilo
// lo controlamos nosotros. Son dos capas: el terreno y, encima, los rótulos.

const TILE = 256;
const ZOOM = 16;
// El mosaico tiene que ser más ancho que el marco, o se ve el negro a los
// lados y el mapa parece corrido. Con 11 teselas de ancho cubre 2816 px, que
// llega hasta las pantallas anchas; de alto bastan 3 (768 px) para un marco
// de 500. El marco es apaisado, así que no tiene sentido una rejilla cuadrada.
const KX = 5;
const KY = 1;
const ESRI = "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas";

/** Coordenadas de tesela (con decimales) para una posición del mundo. */
function aTeselas(lat, lng, z) {
  const n = 2 ** z;
  const x = ((lng + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y = ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

const Pin = () => (
  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
    {/* onda que se expande, como un radar */}
    <motion.span
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#C5A059]"
      animate={{ width: [14, 120], height: [14, 120], opacity: [0.55, 0] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "easeOut" }}
    />
    <motion.span
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#C5A059]"
      animate={{ width: [14, 120], height: [14, 120], opacity: [0.55, 0] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "easeOut", delay: 1.6 }}
    />
    {/* halo fijo y punto */}
    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#C5A059]/15 blur-[6px]" />
    <span className="relative block w-3 h-3 rounded-full bg-[#C5A059] shadow-[0_0_18px_rgba(197,160,89,0.95)] ring-2 ring-[#0C0C0C]" />
  </div>
);

export const StudioMap = ({ label, directions, fachada }) => {
  const teselas = useMemo(() => {
    const { x, y } = aTeselas(STUDIO.lat, STUDIO.lng, ZOOM);
    const cx = Math.floor(x);
    const cy = Math.floor(y);
    const lista = [];
    for (let dy = -KY; dy <= KY; dy++) {
      for (let dx = -KX; dx <= KX; dx++) {
        const tx = cx + dx;
        const ty = cy + dy;
        // Ojo: Esri pide la tesela en orden z/y/x, no z/x/y.
        lista.push({
          key: `${tx}-${ty}`,
          base: `${ESRI}/World_Dark_Gray_Base/MapServer/tile/${ZOOM}/${ty}/${tx}`,
          rotulos: `${ESRI}/World_Dark_Gray_Reference/MapServer/tile/${ZOOM}/${ty}/${tx}`,
          left: (dx + KX) * TILE,
          top: (dy + KY) * TILE,
        });
      }
    }
    // dónde cae el estudio dentro del mosaico
    const px = KX * TILE + (x - cx) * TILE;
    const py = KY * TILE + (y - cy) * TILE;
    return {
      lista,
      px,
      py,
      ancho: (2 * KX + 1) * TILE,
      alto: (2 * KY + 1) * TILE,
    };
  }, []);

  return (
    <div
      className="group relative h-[340px] md:h-[500px] overflow-hidden border border-[#C5A059]/25 bg-[#0C0C0C]"
      data-testid="studio-map"
    >
      {/* mosaico de teselas, centrado en el estudio */}
      <div
        className="absolute ease-out group-hover:scale-[1.06]"
        style={{
          width: teselas.ancho,
          height: teselas.alto,
          left: `calc(50% - ${teselas.px}px)`,
          top: `calc(50% - ${teselas.py}px)`,
          // El acercamiento del hover gira sobre el estudio, no sobre el centro
          // del mosaico: si no, el punto se escapa al agrandarse.
          transformOrigin: `${teselas.px}px ${teselas.py}px`,
          filter: "saturate(0.6) contrast(1.05) brightness(1.05)",
          transition: "transform 1.8s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {teselas.lista.map((t) => (
          <img
            key={t.key}
            src={t.base}
            alt=""
            aria-hidden="true"
            draggable="false"
            className="absolute select-none"
            style={{ left: t.left, top: t.top, width: TILE, height: TILE }}
          />
        ))}
        {/* capa de rótulos: calles y nombres, encima del terreno */}
        {teselas.lista.map((t) => (
          <img
            key={`r-${t.key}`}
            src={t.rotulos}
            alt=""
            aria-hidden="true"
            draggable="false"
            className="absolute select-none"
            style={{ left: t.left, top: t.top, width: TILE, height: TILE }}
          />
        ))}
      </div>

      {/* tinte dorado y viñeta: funde el mapa con el negro de la página */}
      <div className="pointer-events-none absolute inset-0 bg-[#C5A059] mix-blend-overlay opacity-[0.07]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_38%,rgba(12,12,12,0.55)_78%,#0C0C0C_100%)]" />
      <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[#C5A059]/15" />

      <Pin />

      {/* ficha flotante */}
      <div className="absolute left-5 bottom-5 right-5 md:left-7 md:bottom-7 md:right-auto md:max-w-[300px]">
        <div className="border border-[#C5A059]/30 bg-[#0C0C0C]/88 backdrop-blur-md px-6 py-5">
          <p className="text-[0.55rem] uppercase tracking-[0.35em] text-[#C5A059]">{label}</p>
          <p className="mt-3 font-serif text-2xl text-white leading-none">
            APEX <em className="italic text-[#C5A059]">Ride</em>
          </p>
          <p className="mt-2 text-[0.55rem] uppercase tracking-[0.28em] text-zinc-500">
            {(STUDIO.addressLines.es || [])[0]}
          </p>
          <a
            href={mapsDirectionsUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-directions-btn"
            className="mt-4 flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] hover:text-white transition-colors duration-300"
          >
            {directions} <span aria-hidden>↗</span>
          </a>
          {/* La fachada: así reconoce la puerta antes de llegar. */}
          <a
            href={streetViewUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-streetview-btn"
            className="mt-2.5 flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.3em] text-zinc-500 hover:text-[#C5A059] transition-colors duration-300"
          >
            {fachada} <span aria-hidden>↗</span>
          </a>
        </div>
      </div>

      {/* atribución obligatoria de las teselas, discreta */}
      <p className="absolute right-2 bottom-1 text-[0.5rem] text-zinc-700 tracking-wide">
        Esri · HERE · © OpenStreetMap
      </p>
    </div>
  );
};
